import React, { createContext, useContext, useEffect, useReducer } from 'react';
import { products } from '../mock';

const CartContext = createContext(null);

const initialState = { items: [] };

// El carrito se guarda en el navegador para que sobreviva a recargar la página y a ir y
// volver de Mercado Pago (antes vivía solo en memoria y se perdía).
const STORAGE_KEY = 'quinlate_cart';

// Precio vigente por id (las variantes comparten el del modelo). Al recuperar un carrito
// guardado se usa este y no el guardado: si un precio cambió, el carrito muestra y cobra el
// actual; si el producto ya no está en el catálogo, se descarta.
const CURRENT_PRICES = products.reduce((prices, p) => {
  prices[p.id] = p.price;
  (p.variants || []).forEach((v) => {
    prices[v.id] = p.price;
  });
  return prices;
}, {});

function loadCart() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (!saved || !Array.isArray(saved.items)) return initialState;
    const items = saved.items
      .filter(
        (i) =>
          i &&
          typeof i.cartId === 'string' &&
          typeof i.name === 'string' &&
          typeof i.id === 'string' &&
          CURRENT_PRICES[i.id]
      )
      .map((i) => ({
        cartId: i.cartId,
        id: i.id,
        name: i.name,
        price: CURRENT_PRICES[i.id],
        size: typeof i.size === 'string' ? i.size : null,
        qty: Math.min(Math.max(parseInt(i.qty, 10) || 1, 1), 10),
      }));
    return { items };
  } catch {
    // dato corrupto, modo privado o almacenamiento bloqueado: se empieza con carrito vacío
    return initialState;
  }
}

function reducer(state, action) {
  switch (action.type) {
    case 'ADD': {
      const existing = state.items.find((i) => i.cartId === action.item.cartId);
      if (existing) {
        return {
          items: state.items.map((i) =>
            i.cartId === action.item.cartId ? { ...i, qty: i.qty + 1 } : i
          ),
        };
      }
      return { items: [...state.items, { ...action.item, qty: 1 }] };
    }
    case 'REMOVE':
      return { items: state.items.filter((i) => i.cartId !== action.cartId) };
    case 'CLEAR':
      return initialState;
    default:
      return state;
  }
}

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, undefined, loadCart);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ items: state.items }));
    } catch {
      // almacenamiento lleno o bloqueado: el carrito sigue funcionando en memoria
    }
  }, [state.items]);

  const addItem = (item) => dispatch({ type: 'ADD', item });
  const removeItem = (cartId) => dispatch({ type: 'REMOVE', cartId });
  const clear = () => dispatch({ type: 'CLEAR' });

  return (
    <CartContext.Provider value={{ items: state.items, addItem, removeItem, clear }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart debe usarse dentro de <CartProvider>');
  return ctx;
};
